import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Home Surge Protection Systems',
  provider: {
    '@type': 'ElectricalContractor',
    name: 'Electrician Solutions Fresno',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Fresno',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Fresno',
    '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
  },
  description:
    'Professional whole-home surge protection installation in Fresno CA including panel-mounted surge protectors, point-of-use protection, lightning surge suppression, and electrical equipment protection by licensed C-10 electricians',
  url: 'https://electriciansolutionsfresno.com/services/residential-electrical/home-surge-protection',
};

export default function HomeSurgeProtectionPage() {
  const features = [
    {
      icon: 'ri-flashlight-line',
      title: 'Whole-Home Surge Protection',
      description:
        'Install panel-mounted whole-home surge protectors at your electrical panel to protect all circuits and appliances from power surges and lightning strikes in Fresno homes',
    },
    {
      icon: 'ri-tv-line',
      title: 'Electronics Protection',
      description:
        'Protect expensive electronics including TVs, computers, gaming systems, home theater equipment, and smart home devices from damaging voltage spikes and power surges',
    },
    {
      icon: 'ri-home-wifi-line',
      title: 'HVAC & Appliance Protection',
      description:
        'Safeguard major appliances and HVAC systems — air conditioners, furnaces, refrigerators, washers, dryers — from power surge damage that can cause costly repairs or replacements',
    },
    {
      icon: 'ri-thunderstorms-line',
      title: 'Lightning Surge Suppression',
      description:
        'Advanced surge protection systems that defend against lightning-induced power surges — critical for Fresno homes during Central Valley thunderstorm season',
    },
    {
      icon: 'ri-plug-line',
      title: 'Point-of-Use Surge Protection',
      description:
        'Install dedicated surge-protected outlets for sensitive equipment like home offices, entertainment centers, and computer workstations throughout your Fresno home',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Multi-Layer Protection',
      description:
        'Comprehensive surge protection strategy combining whole-home panel protection with point-of-use devices for maximum defense against all types of power surges',
    },
    {
      icon: 'ri-battery-charge-line',
      title: 'EV Charger Surge Protection',
      description:
        'Protect expensive electric vehicle charging equipment from power surges and voltage spikes — essential for Fresno homeowners with Level 2 EV chargers',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Smart Home System Protection',
      description:
        'Safeguard smart home hubs, security systems, automated lighting, thermostats, and connected devices from surge damage that can disrupt your entire smart home network',
    },
  ];

  const whatsIncluded = [
    'Free home electrical assessment and surge protection consultation for Fresno properties',
    'Licensed C-10 electricians with expertise in surge protection system installation',
    'High-quality surge protectors from trusted brands — Eaton, Siemens, Square D, and Leviton',
    'Whole-home panel-mounted surge protection with warranty coverage',
    'Point-of-use surge protection for sensitive electronics and equipment',
    'Professional installation meeting California electrical codes and NEC standards',
    'Complete testing and verification of surge protection system operation',
    'Warranty on all labor and equipment — protecting your investment and peace of mind',
  ];

  return (
    <>
      <SEOHead
        title="Whole Home Surge Protection Fresno CA | Surge Protector Installation"
        description="Professional whole-home surge protection installation in Fresno, CA. Panel-mounted surge protectors, lightning protection, electronics and appliance protection. Licensed C-10 electricians. Free quotes."
        canonical="/services/residential-electrical/home-surge-protection"
        keywords="whole home surge protection Fresno, surge protector installation Fresno CA, lightning surge protection Fresno, panel surge protector Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Home Surge Protection Systems — Fresno, CA"
        heroSubtitle="Protect your electronics, appliances & HVAC from power surges — whole-home surge protection"
        heroImage="https://public.readdy.ai/ai/img_res/da989e1dbeae1ffb19499567d4958fcf.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrician%20hands%20installing%20panel%20mounted%20surge%20protection%20device%20connecting%20wires%20to%20circuit%20breaker%20panel%20with%20tools%20and%20equipment%20visible%20bright%20indoor%20lighting%20California%20home&width=700&height=600&seq=surge-fresno-install-03&orientation=portrait"
        tagline="Trusted by 500+ Fresno Homeowners"
        fresnoContent="Protecting Fresno homes from damaging power surges with professional whole-home surge protection systems. Whether you're safeguarding expensive electronics in a Tower District home, protecting HVAC equipment in a Woodward Park property, defending against lightning strikes in Fig Garden, or securing smart home systems in Clovis, our licensed C-10 electricians install panel-mounted surge protectors and point-of-use devices throughout your home. Power surges from lightning strikes, utility grid switching, and large appliance cycling can damage or destroy electronics, appliances, HVAC systems, and smart home equipment — costing thousands in repairs or replacements. Fresno's Central Valley location experiences thunderstorms and power fluctuations that put homes at risk. We install whole-home surge protection at your electrical panel plus point-of-use protection for sensitive equipment, providing comprehensive defense against all types of power surges."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What is whole-home surge protection and how does it work?',
            answer:
              'Whole-home surge protection is a device installed at your electrical panel that protects all circuits and connected equipment from power surges. When a voltage spike occurs (from lightning, utility grid switching, or large appliances), the surge protector diverts excess voltage to ground, preventing it from reaching your electronics and appliances. This provides protection for everything in your Fresno home — TVs, computers, HVAC systems, refrigerators, washers, dryers, and more.',
          },
          {
            question: 'How much does whole-home surge protection cost in Fresno?',
            answer:
              'Whole-home surge protection installation typically costs $400–$800 including equipment and labor, depending on your electrical panel type and surge protector model. High-end surge protectors with advanced features and higher joule ratings cost more but provide superior protection. Point-of-use surge-protected outlets cost $150–$250 per location. Considering that a single power surge can cause thousands of dollars in damage, surge protection is an excellent investment.',
          },
          {
            question: 'Do I need surge protection if I have power strips with surge protection?',
            answer:
              'Yes, power strip surge protectors provide only limited point-of-use protection and cannot defend against large surges from lightning strikes or utility grid issues. Whole-home surge protection installed at your electrical panel provides comprehensive protection for all circuits, including hardwired appliances, HVAC systems, and equipment that cannot be plugged into power strips.',
          },
          {
            question: 'What causes power surges in Fresno homes?',
            answer:
              "Power surges have several causes: lightning strikes (direct or nearby), utility grid switching and power restoration after outages, large appliances cycling on/off (AC units, refrigerators, pool pumps), and faulty wiring. Fresno's Central Valley location experiences thunderstorms during certain seasons, and the local power grid can experience fluctuations. Even small surges over time can degrade electronics and shorten appliance lifespan.",
          },
          {
            question: 'Will surge protection protect against lightning strikes?',
            answer:
              'Whole-home surge protection provides significant defense against lightning-induced power surges, but no system can guarantee 100% protection from direct lightning strikes (which are extremely rare). Most lightning damage occurs from nearby strikes that send surges through power lines into homes. Panel-mounted surge protectors are designed to handle these large voltage spikes and divert them safely to ground.',
          },
          {
            question: 'How long do surge protectors last and do they need replacement?',
            answer:
              "Whole-home surge protectors typically last 5-10 years depending on the number and severity of surges they handle. Most modern surge protectors have LED indicator lights that show when they're working properly and alert you when they need replacement. After a major surge event (like a nearby lightning strike), surge protectors should be inspected and may need replacement even if the indicator light is still on.",
          },
        ]}
        serviceSlug="home-surge-protection"
      />
    </>
  );
}
